import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import BrowserMockup3D from "../BrowserMockup3D";

// Import screenshot images
const homeScreenshot = "/assets/screenshots/home page.png";                 // Personalized dashboard
const coursesScreenshot = "/assets/screenshots/courses page.png";           // Course catalog
const vocabularyScreenshot1 = "/assets/screenshots/study deck page.png";      // Study interface
const vocabularyScreenshot2 = "/assets/screenshots/decks page.png";          // Deck selection
const spacedRepetitionScreenshot1 = "/assets/screenshots/learning deck.png"; // Learning progress
const spacedRepetitionScreenshot2 = "/assets/screenshots/statistics page.png"; // Progress stats
const importstep2 = "/assets/screenshots/import step 2.png";                 // Import step 2
const importstep3 = "/assets/screenshots/import step 3.png";                 // Import step 3
const profilepage = "/assets/screenshots/profile page.png";                 // Profile page
const statisticsPage = "/assets/screenshots/statistics page.png";           // Statistics overview
const resourcesPage = "/assets/screenshots/resources page.png";             // Resources page screenshot
const roadmapsPage = "/assets/screenshots/roadmaps page.png";               // Roadmaps page screenshot
const forumsPage = "/assets/screenshots/forums page.png";                   // Forums page screenshot
const openForum = "/assets/screenshots/open forum.png";                     // Open forum discussion screenshot

const MockupPair = ({
    leftImage,
    rightImage,
    leftAlt,
    rightAlt,
    isMobile,
    mockupWidth,
    mockupHeight,
    desktopGap
}) => {
    if (isMobile) {
        const mobileWidth = Math.min(420, typeof window !== 'undefined' ? window.innerWidth * 0.85 : 360);
        return (
            <Box
                display="flex"
                flexDirection="column"
                gap={3}
                alignItems="center"
                width="100%"
            >
                <BrowserMockup3D
                    imageSrc={leftImage}
                    direction="center"
                    alt={leftAlt}
                    width={mobileWidth}
                    height={240}
                />
                <BrowserMockup3D
                    imageSrc={rightImage}
                    direction="center"
                    alt={rightAlt}
                    width={mobileWidth}
                    height={240}
                />
            </Box>
        );
    }

    return (
        <Box
            display="flex"
            gap={desktopGap}
            justifyContent="center"
            alignItems="center"
            flexWrap="nowrap"
            width="100%"
            maxWidth="1400px"
            sx={{ overflowX: 'visible' }}
        >
            <BrowserMockup3D
                imageSrc={leftImage}
                direction="left"
                alt={leftAlt}
                width={mockupWidth}
                height={mockupHeight}
            />
            <BrowserMockup3D
                imageSrc={rightImage}
                direction="right"
                alt={rightAlt}
                width={mockupWidth}
                height={mockupHeight}
            />
        </Box>
    );
};

const MidWithMockups = () => {
    const theme = useTheme();
    const navigate = useNavigate();

    // Breakpoint Tiers
    const isMobile = useMediaQuery("(max-width: 899px)");
    const isSmallPC = useMediaQuery("(min-width: 900px) and (max-width: 1279px)");
    const isMediumPC = useMediaQuery("(min-width: 1280px) and (max-width: 1535px)");
    const isLargePC = useMediaQuery("(min-width: 1536px)");

    // Responsive dimensions per tier
    const mockupWidth = isSmallPC ? 400 : isMediumPC ? 490 : 580;
    const mockupHeight = isSmallPC ? 275 : isMediumPC ? 335 : 395;
    const desktopGap = isSmallPC ? 3 : 4;

    const primaryLight = theme.palette.primary.light;

    return (
        <Box sx={{ overflowX: 'hidden' }}>
            {/* Dashboard & Courses Section */}
            <Box
                id="features"
                width="100%"
                padding={isMobile ? "2rem 4%" : "3rem 4%"}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                gap={isMobile ? "2rem" : "3rem"}
            >
                <Box maxWidth={isMobile ? "100%" : "800px"}>
                    <Typography
                        component="h2"
                        variant={isMobile ? "h4" : "h3"}
                        color="white"
                        sx={{
                            fontWeight: 'bold',
                            mb: 2,
                            fontSize: isMobile ? '2rem' : '2.25rem'
                        }}
                    >
                        Everything You're Learning, In One Place
                    </Typography>
                    <Typography
                        variant="body1"
                        color={primaryLight}
                        sx={{
                            fontSize: isMobile ? '1.1rem' : '1.05rem',
                            lineHeight: 1.6,
                            maxWidth: '700px',
                            margin: '0 auto'
                        }}
                    >
                        See all your courses and daily progress on a personalized dashboard, then follow step-by-step courses that take you from absolute beginner to full proficiency.
                    </Typography>
                </Box>

                <MockupPair
                    leftImage={homeScreenshot}
                    rightImage={coursesScreenshot}
                    leftAlt="Personalized Learning Dashboard"
                    rightAlt="Step-by-Step Courses"
                    isMobile={isMobile}
                    mockupWidth={mockupWidth}
                    mockupHeight={mockupHeight}
                    desktopGap={desktopGap}
                />
            </Box>

            {/* Spacing */}
            <Box height={isMobile ? "3rem" : "5rem"} />

            {/* Knowledge Building Section */}
            <Box
                width="100%"
                padding={isMobile ? "2rem 4%" : "3rem 4%"}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                gap={isMobile ? "2rem" : "3rem"}
            >
                <Box maxWidth={isMobile ? "100%" : "800px"}>
                    <Typography 
                        component="h2"
                        variant={isMobile ? "h4" : "h3"}
                        color="white"
                        sx={{
                            fontWeight: 'bold',
                            mb: 2,
                            fontSize: isMobile ? '2rem' : '2.25rem'
                        }}
                    >
                        Knowledge Building
                    </Typography>
                    <Typography 
                        variant="body1" 
                        color={primaryLight} 
                        sx={{
                            fontSize: isMobile ? '1.1rem' : '1.05rem',
                            lineHeight: 1.6,
                            maxWidth: '700px',
                            margin: '0 auto'
                        }}
                    >
                        Build your knowledge in any subject efficiently with our comprehensive collections, examples, and structured learning paths.
                    </Typography>
                </Box>

                <MockupPair
                    leftImage={vocabularyScreenshot1}
                    rightImage={vocabularyScreenshot2}
                    leftAlt="Study Deck Interface"
                    rightAlt="Decks Overview Page"
                    isMobile={isMobile}
                    mockupWidth={mockupWidth}
                    mockupHeight={mockupHeight}
                    desktopGap={desktopGap}
                />
            </Box>

            {/* Spacing */}
            <Box height={isMobile ? "3rem" : "5rem"} />

            {/* Spaced Repetition Section */}
            <Box
                width="100%"
                padding={isMobile ? "2rem 4%" : "3rem 4%"}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                gap={isMobile ? "2rem" : "3rem"}
            >
                <Box maxWidth={isMobile ? "100%" : "800px"}>
                    <Typography 
                        variant={isMobile ? "h4" : "h3"}
                        color="white"
                        sx={{
                            fontWeight: 'bold',
                            mb: 2,
                            fontSize: isMobile ? '2rem' : '2.25rem'
                        }}
                    >
                        Spaced Repetition
                    </Typography>
                    <Typography 
                        variant="body1" 
                        color={primaryLight}
                        sx={{
                            fontSize: isMobile ? '1.1rem' : '1.05rem',
                            lineHeight: 1.6,
                            maxWidth: '700px',
                            margin: '0 auto'
                        }}
                    >
                        Master any subject with our intelligent spaced repetition system.
                    </Typography>
                </Box>

                <MockupPair
                    leftImage={spacedRepetitionScreenshot1}
                    rightImage={spacedRepetitionScreenshot2}
                    leftAlt="Learning Deck Interface"
                    rightAlt="Statistics and Progress Tracking"
                    isMobile={isMobile}
                    mockupWidth={mockupWidth}
                    mockupHeight={mockupHeight}
                    desktopGap={desktopGap}
                />
            </Box>

            {/* Spacing */}
            <Box height={isMobile ? "3rem" : "5rem"} />

            {/* Import System Section */}
            <Box
                id="import"
                width="100%"
                padding={isMobile ? "2rem 4%" : "3rem 4%"}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                gap={isMobile ? "2rem" : "3rem"}
            >
                <Box maxWidth={isMobile ? "100%" : "800px"}>
                    <Typography 
                        variant={isMobile ? "h4" : "h3"}
                        color="white"
                        sx={{
                            fontWeight: 'bold',
                            mb: 2,
                            fontSize: isMobile ? '2rem' : '2.25rem'
                        }}
                    >
                        Easy Import System
                    </Typography>
                    <Typography 
                        variant="body1" 
                        color={primaryLight}
                        sx={{
                            fontSize: isMobile ? '1.1rem' : '1.05rem',
                            lineHeight: 1.6,
                            maxWidth: '700px',
                            margin: '0 auto'
                        }}
                    >
                        Import your existing flashcards and study materials with our streamlined import process. Compatible with popular formats and platforms.
                    </Typography>
                </Box>

                <MockupPair
                    leftImage={importstep2}
                    rightImage={importstep3}
                    leftAlt="Import Step 2 - Field Mapping"
                    rightAlt="Import Step 3 - Confirmation"
                    isMobile={isMobile}
                    mockupWidth={mockupWidth}
                    mockupHeight={mockupHeight}
                    desktopGap={desktopGap}
                />
            </Box>

            {/* Spacing */}
            <Box height={isMobile ? "3rem" : "5rem"} />

            {/* Profile & Statistics Section */}
            <Box
                id="progress_tracking"
                width="100%"
                padding={isMobile ? "2rem 4%" : "3rem 4%"}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                gap={isMobile ? "2rem" : "3rem"}
            >
                <Box maxWidth={isMobile ? "100%" : "800px"}>
                    <Typography 
                        variant={isMobile ? "h4" : "h3"}
                        color="white"
                        sx={{
                            fontWeight: 'bold',
                            mb: 2,
                            fontSize: isMobile ? '2rem' : '2.25rem'
                        }}
                    >
                        Progress Tracking
                    </Typography>
                    <Typography 
                        variant="body1" 
                        color={primaryLight}
                        sx={{
                            fontSize: isMobile ? '1.1rem' : '1.05rem',
                            lineHeight: 1.6,
                            maxWidth: '700px',
                            margin: '0 auto'
                        }}
                    >
                        Monitor your learning journey with detailed analytics, personalized profiles, and comprehensive progress statistics to stay motivated and track improvements.
                    </Typography>
                </Box>

                <MockupPair
                    leftImage={profilepage}
                    rightImage={statisticsPage}
                    leftAlt="User Profile Page"
                    rightAlt="Detailed Statistics Dashboard"
                    isMobile={isMobile}
                    mockupWidth={mockupWidth}
                    mockupHeight={mockupHeight}
                    desktopGap={desktopGap}
                />
            </Box>

            {/* Spacing */}
            <Box height={isMobile ? "3rem" : "5rem"} />

            {/* Learning Resources Section */}
            <Box
                id="resources"
                width="100%"
                padding={isMobile ? "2rem 4%" : "3rem 4%"}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                gap={isMobile ? "2rem" : "3rem"}
            >
                <Box maxWidth={isMobile ? "100%" : "1000px"}>
                    <Typography 
                        variant={isMobile ? "h4" : "h3"}
                        color="white"
                        sx={{
                            fontWeight: 'bold',
                            mb: 2,
                            fontSize: isMobile ? '2rem' : '2.25rem'
                        }}
                    >
                        Learning Resources
                    </Typography>
                    <Typography 
                        variant="body1" 
                        color={primaryLight}
                        sx={{
                            fontSize: isMobile ? '1.1rem' : '1.05rem',
                            lineHeight: 1.6,
                            maxWidth: '800px',
                            margin: '0 auto'
                        }}
                    >
                        Access a comprehensive library of curated learning materials, study guides, and resources to enhance your learning experience.
                    </Typography>
                </Box>

                <MockupPair
                    leftImage={resourcesPage}
                    rightImage={roadmapsPage}
                    leftAlt="Learning Resources Page"
                    rightAlt="Structured Learning Roadmaps"
                    isMobile={isMobile}
                    mockupWidth={mockupWidth}
                    mockupHeight={mockupHeight}
                    desktopGap={desktopGap}
                />
            </Box>

            {/* Spacing */}
            <Box height={isMobile ? "3rem" : "5rem"} />

            {/* Community Forums Section */}
            <Box
                id="forums"
                width="100%"
                padding={isMobile ? "2rem 4%" : "3rem 4%"}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                textAlign="center"
                gap={isMobile ? "2rem" : "3rem"}
            >
                <Box maxWidth={isMobile ? "100%" : "800px"}>
                    <Typography 
                        variant={isMobile ? "h4" : "h3"}
                        color="white"
                        sx={{
                            fontWeight: 'bold',
                            mb: 2,
                            fontSize: isMobile ? '2rem' : '2.25rem'
                        }}
                    >
                        Community Forums
                    </Typography>
                    <Typography 
                        variant="body1" 
                        color={primaryLight}
                        sx={{
                            fontSize: isMobile ? '1.1rem' : '1.05rem',
                            lineHeight: 1.6,
                            maxWidth: '700px',
                            margin: '0 auto'
                        }}
                    >
                        Join vibrant discussions with fellow learners in our dedicated Uben and Kindle forums. Share experiences, get help with technical issues, discover new study methods, and connect with a global community of language enthusiasts.
                    </Typography>
                </Box>

                <MockupPair
                    leftImage={forumsPage}
                    rightImage={openForum}
                    leftAlt="Community Forums Overview"
                    rightAlt="Active Forum Discussion"
                    isMobile={isMobile}
                    mockupWidth={mockupWidth}
                    mockupHeight={mockupHeight}
                    desktopGap={desktopGap}
                />
            </Box>
        </Box>
    );
};

export default MidWithMockups;
